import { useState } from 'react'
import { updatePresentPosition } from '../services/claimsApi'
import { useSsoToken } from './useSsoToken'
import { getApiErrorMessage } from '../utils/errorMessages'
import { containsMarkup, MARKUP_VALIDATION_MESSAGE } from '../utils/textValidation'

export const PRESENT_POSITION_NOTES_MAX_LENGTH = 2000

export type FieldStatus = 'idle' | 'saving' | 'success' | 'error'

export interface FieldState {
  status: FieldStatus
  message: string | null
}

export interface PresentPositionEdit {
  statusId: number | null
  notes: string | null
  setStatusId: (next: number | null) => void
  setNotes: (next: string | null) => void
  isDirty: boolean
  // The single owner of description validity: the editor renders this one message live,
  // and Save stays disabled while it is set. Previously the rule lived in both the editor
  // and save(), so the same text appeared twice on screen (MSD-736).
  validationError: string | null
  state: FieldState
  save: () => Promise<void>
}

// Length is checked before markup so an over-long draft reports that first.
function getDescriptionValidationError(notes: string | null): string | null {
  if (notes == null) return null
  if (notes.length > PRESENT_POSITION_NOTES_MAX_LENGTH) {
    return `Description must be ${PRESENT_POSITION_NOTES_MAX_LENGTH} characters or fewer.`
  }
  if (containsMarkup(notes)) return MARKUP_VALIDATION_MESSAGE
  return null
}

// The claim's stored status id and notes, used to pre-fill the editor (MSD-538).
export interface PresentPositionValues {
  statusId: number | null
  notes: string | null
}

const EMPTY_VALUES: PresentPositionValues = { statusId: null, notes: null }

// Tracks the Present Position editor's pending selection (status id + notes) and
// saves it via PATCH /api/claims/{claimNumber}/present-position. The editor opens
// pre-filled with `initial` (the claim's presentPositionId/presentPositionNote),
// which arrives a render or two after mount — hence the undefined drafts below:
// they mean "the user has not touched this field yet", so the claim's value shows
// through until they do, without an effect to copy it into state.
export function usePresentPositionEdit(claimNumber: string, initial?: PresentPositionValues): PresentPositionEdit {
  const { getToken } = useSsoToken()
  const [statusIdDraft, setStatusIdDraft] = useState<number | null | undefined>(undefined)
  const [notesDraft, setNotesDraft] = useState<string | null | undefined>(undefined)
  // Last successfully saved values — until a save happens the claim's own values
  // are the baseline, so simply opening the editor is not a change.
  const [saved, setSaved] = useState<PresentPositionValues | null>(null)
  const [state, setState] = useState<FieldState>({ status: 'idle', message: null })

  const baseline = saved ?? initial ?? EMPTY_VALUES
  const statusId = statusIdDraft === undefined ? baseline.statusId : statusIdDraft
  const notes = notesDraft === undefined ? baseline.notes : notesDraft

  // Present Position is required, so notes typed without a selected status
  // are not yet a submittable change.
  const isDirty = statusId !== null && (statusId !== baseline.statusId || notes !== baseline.notes)

  // Validate the effective value, not the draft: a pre-filled note that is over-long or
  // contains markup must be flagged as soon as the editor opens (MSD-538 + MSD-736).
  const validationError = getDescriptionValidationError(notes)

  const setStatusId = (next: number | null) => {
    setStatusIdDraft(next)
    setState({ status: 'idle', message: null })
  }

  const setNotes = (next: string | null) => {
    setNotesDraft(next)
    setState({ status: 'idle', message: null })
  }

  const save = async () => {
    // Save is disabled while validationError is set and the editor already shows it, so
    // this only guards a programmatic call — it deliberately writes no second message.
    if (validationError != null) return
    if (!isDirty) return
    setState({ status: 'saving', message: null })
    try {
      const token = await getToken()
      await updatePresentPosition(claimNumber, { currentStatusId: statusId, currentStatusNotes: notes }, token)
      setSaved({ statusId, notes })
      // The backend returns a generic "Claim attributes updated successfully." message;
      // name the field that was saved instead so the confirmation is unambiguous.
      setState({ status: 'success', message: 'Present position updated successfully.' })
    } catch (err) {
      console.error('[PresentPositionEdit] Save failed:', err)
      setState({ status: 'error', message: getApiErrorMessage(err, 'Save failed. Please try again.') })
    }
  }

  return { statusId, notes, setStatusId, setNotes, isDirty, validationError, state, save }
}
